/**
 * @typedef {{id: string, type: 'video', duration: number, format: 'mp4' | 'youtube', source: string}} MediaMetadataElement
 * @typedef {{[pageId: string]: MediaMetadataElement[]}} MediaMetadata
 * @typedef {number[]} PagesOrder
 */

import Store from '@store';
import { saveMetadata } from 'src/store/actions/metadata.action';

export class MediaFactory {
  #hasChanges = false;

  async init () {
    this.#addEventListeners();
  }

  /**
   *
   * @returns {HTMLElement[]}
   */
  #getVideosFromCurrentPage () {
    const videos = INKIVE.DesignFacade.get().filter(element => element.type === 'video' && !element.youtube);
    if (videos.length === 0) {
      return [];
    }
    const videosSelector = videos.map(video => `#${video.id}`).join(', ');
    return [...document.querySelectorAll(videosSelector)];
  }

  #historyListener () {
    const videosFromCurrentPage = this.#getVideosFromCurrentPage();
    const metadata = videosFromCurrentPage
      .filter(video => video.getAttribute('data-video-type') === 'mp4')
      .map(video => this.#fromVideoHtmlToMetadataElement(video));

    this.setMetadataForPage(INKIVE.PageFactory.getCurrentPageId(), metadata);
  }

  /**
   *
   * @param {HTMLElement} videoElement
   * @return {MediaMetadataElement}
   */
  #fromVideoHtmlToMetadataElement (videoElement) {
    return {
      duration: Number(videoElement.dataset.duration),
      format: videoElement.dataset.videoType,
      id: videoElement.getAttribute('id'),
      type: 'video',
      source: videoElement.getAttribute('xlink:href')
    };
  }

  /**
   *
   * @param {{id: string}} params
   */
  #videoLoadMetadataListener (params) {
    const metadata = this.getMediaMetadataInPage();
    const videoElement = INKIVE.DesignFacade.getSelectedElements().find(video => video.id === params.id);
    if (!videoElement) {
      return;
    }
    const metadataVideo = metadata.find(video => video.id === params.id);
    if (metadataVideo) {
      this.setMetadataForPage(INKIVE.PageFactory.getCurrentPageId(),
        [...metadata.filter(data => data.id !== params.id), this.#fromVideoHtmlToMetadataElement(videoElement)]);
    }
  }

  #addEventListeners () {
    INKIVE.EditorFacade.on('history.change', () => this.#historyListener());
    INKIVE.EditorFacade.on('history.undo', () => this.#historyListener());
    INKIVE.EditorFacade.on('history.redo', () => this.#historyListener());
    INKIVE.DesignFacade.on('video.loadedmetadata', (params) => this.#videoLoadMetadataListener(params));
  }

  /**
   * @private
   * @returns {MediaMetadata}
   */
  get #mediaMetadata () {
    return Store.getState().metadata.media ?? {};
  }

  /**
   *
   * @returns {PagesOrder}
   */
  get #allPages () {
    return Store.getState().editor.pagesOrder ?? [];
  }

  /**
   *
   * @param {string} pageId
   * @returns {MediaMetadataElement[]}
   */
  getMediaMetadataInPage (pageId = INKIVE.PageFactory.getCurrentPageId()) {
    return this.#mediaMetadata[pageId] ?? [];
  }

  /**
   *
   * @returns {boolean}
   */
  hasChanges () {
    return this.#hasChanges;
  }

  /**
   *
   * @param {string} pageId
   * @param {MediaMetadataElement[]} metadata
   * @returns {Promise<void>}
   */
  async setMetadataForPage (pageId, metadata) {
    const allPages = this.#allPages;
    if (!allPages.includes(Number(pageId))) {
      return;
    }
    const newMetadata = {
      media: {
        ...this.#mediaMetadata,
        [pageId]: [...metadata]
      }
    };

    if (metadata.length === 0) {
      delete newMetadata.media[pageId];
    }

    if (Object.keys(newMetadata.media).length === 0) {
      newMetadata.media = null;
    }

    await Store.dispatch(saveMetadata(newMetadata));

    this.#hasChanges = true;
    INKIVE.hasEditorUnsavedChanges = true;
  }
}
